import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, Download, ArrowLeft, Leaf, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const iso = (d) => d.toISOString().slice(0, 10);

export default function WeeklySummary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [milestones, setMilestones] = useState(null);
  const [profile, setProfile] = useState({});
  const [period, setPeriod] = useState("weekly");
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 864e5)));
  const [to, setTo] = useState(iso(today));

  useEffect(() => {
    api.get("/logs").then(({ data }) => { setLogs(data.logs); setMilestones(data.milestones); }).catch(() => {});
    api.get("/taper/profile").then(({ data }) => setProfile(data || {})).catch(() => {});
  }, []);

  const bounds = () => {
    if (period === "yearly") return [new Date(Date.now() - 365 * 864e5), today];
    if (period === "custom") return [new Date(from), new Date(to)];
    return [new Date(Date.now() - 7 * 864e5), today];
  };
  const [start, end] = bounds();
  const rows = logs.filter((l) => { const d = new Date(l.date); return d >= start && d <= end; });
  const avg = (arr) => arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length) : null;
  const avgMood = avg(rows.filter((l) => l.mood != null).map((l) => l.mood));
  const avgSleep = avg(rows.filter((l) => l.sleep != null).map((l) => l.sleep));
  const doses = rows.filter((l) => l.dose_mg != null);
  const range = `${start.toLocaleDateString("en-GB")} – ${end.toLocaleDateString("en-GB")}`;
  const currentBenzo = profile.current_benzo || profile.starting_benzo || "";
  const label = period === "yearly" ? "Yearly" : period === "custom" ? "Custom-period" : "Weekly";

  const downloadCsv = () => {
    const header = ["Date", "Dose", "Benzo", "Mood (1-5)", "Sleep (1-5)", "Symptoms", "Other substances", "Notes"];
    const meta = [
      [`TaperPath ${label} summary`], [`Prepared for`, user?.display_name || ""], [`Period`, range],
      [`Started on`, profile.starting_benzo ? `${profile.starting_benzo}${profile.starting_dose != null ? ` at ${profile.starting_dose} mg` : ""}` : "—"],
      [`Currently tapering with`, currentBenzo || "—"], [],
    ];
    const rowsData = rows.map((l) => [l.date, l.dose_mg != null ? `${l.dose_mg} mg` : "", currentBenzo, l.mood ?? "", l.sleep ?? "",
      (l.symptoms || []).join("; "), (l.other_substances || []).join("; "), (l.notes || "").replace(/\n/g, " ")]);
    const csv = [...meta, header, ...rowsData].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `taperpath-${label.toLowerCase()}-${iso(today)}.csv`;
    a.click();
  };

  const [gpEmail, setGpEmail] = useState("");
  const payload = () => ({ period, from_date: from, to_date: to });
  const emailMe = async () => {
    try { await api.post("/summary/email", payload()); toast.success("Summary emailed to you."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const emailGp = async () => {
    if (!gpEmail) { toast.error("Enter your GP or support worker's email."); return; }
    try { await api.post("/summary/email", { ...payload(), to_email: gpEmail }); toast.success(`Summary sent to ${gpEmail}.`); setGpEmail(""); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const PBtn = ({ v, children }) => (
    <button data-testid={`period-${v}`} onClick={() => setPeriod(v)}
      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${period === v ? "bg-[#4A7c59] text-white" : "bg-muted text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>{children}</button>
  );

  return (
    <div className="mx-auto max-w-3xl">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate("/app/tracker")} data-testid="back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
        <div className="flex flex-wrap gap-2">
          {user?.email && <Button data-testid="email-summary" onClick={emailMe} variant="outline" className="rounded-full border-[#4A7c59]/30 text-[#4A7c59]"><Mail className="mr-2 h-4 w-4" /> Email to me</Button>}
          <Button data-testid="download-csv" onClick={downloadCsv} variant="outline" className="rounded-full border-[#4A7c59]/30 text-[#4A7c59]"><Download className="mr-2 h-4 w-4" /> Download CSV</Button>
          <Button data-testid="print-summary" onClick={() => window.print()} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Printer className="mr-2 h-4 w-4" /> Print / Save PDF</Button>
        </div>
      </div>

      <div className="no-print mb-4 flex flex-wrap items-center gap-2" data-testid="period-switch">
        <PBtn v="weekly">Weekly</PBtn>
        <PBtn v="yearly">Yearly</PBtn>
        <PBtn v="custom">Custom</PBtn>
        {period === "custom" && (
          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid="from-date" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="w-40 rounded-xl" />
            <span className="text-muted-foreground">to</span>
            <Input data-testid="to-date" type="date" value={to} min={from} max={iso(today)} onChange={(e) => setTo(e.target.value)} className="w-40 rounded-xl" />
          </div>
        )}
      </div>

      <div className="no-print mb-6 rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid="gp-share">
        <Label className="flex items-center gap-2 font-head font-semibold text-[#2C363F]"><Mail className="h-4 w-4 text-[#4A7c59]" /> Share with your GP or support worker</Label>
        <p className="mt-1 text-sm text-muted-foreground">Send this {label.toLowerCase()} summary directly to a professional to discuss your progress.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Input data-testid="gp-email" type="email" value={gpEmail} onChange={(e) => setGpEmail(e.target.value)} placeholder="gp@practice.nhs.uk" className="min-w-52 flex-1 rounded-xl py-6" />
          <Button data-testid="send-gp" onClick={emailGp} className="rounded-full bg-[#4A7c59] py-6 hover:bg-[#3B6347]"><Mail className="mr-2 h-4 w-4" /> Send to them</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-8 shadow-sm sm:p-12" id="summary">
        <div className="mb-6 flex items-center gap-2 border-b border-border pb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#4A7c59] text-white"><Leaf className="h-5 w-5" /></div>
          <div><div className="font-head text-xl font-bold text-[#2C363F]">TaperPath</div><div className="text-xs text-muted-foreground">{label} progress summary</div></div>
        </div>

        <p className="text-sm text-muted-foreground">Prepared for: <span className="font-medium text-[#2C363F]">{user?.display_name}</span></p>
        <p className="text-sm text-muted-foreground">Period: {range}</p>
        {(profile.starting_benzo || currentBenzo) && (
          <div className="mt-3 rounded-xl bg-[#8FB8DE]/15 p-4 text-sm text-[#2C363F]" data-testid="taper-details">
            {profile.starting_benzo && <p>Started on: <span className="font-medium">{profile.starting_benzo}{profile.starting_dose != null ? ` at ${profile.starting_dose} mg` : ""}</span>{profile.start_date ? ` (from ${new Date(profile.start_date).toLocaleDateString("en-GB")})` : ""}</p>}
            {currentBenzo && <p>Currently tapering with: <span className="font-medium">{currentBenzo}</span></p>}
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Days logged" value={rows.length} />
          <Stat label="Avg mood" value={avgMood ? `${avgMood.toFixed(1)}/5` : "—"} />
          <Stat label="Avg sleep" value={avgSleep ? `${avgSleep.toFixed(1)}/5` : "—"} />
          <Stat label="Latest dose" value={doses.length ? `${doses[doses.length - 1].dose_mg} mg${currentBenzo ? ` ${currentBenzo}` : ""}` : "—"} />
        </div>

        {milestones && (
          <p className="mt-5 rounded-xl bg-[#4A7c59]/10 p-4 text-sm text-[#2C363F]">
            Overall: {milestones.days_tracked} days tracked{milestones.total_reduction > 0 ? `, ${currentBenzo || "dose"} down ${milestones.total_reduction} mg (${milestones.percent_reduced}%) since starting${profile.starting_benzo ? ` on ${profile.starting_benzo}` : ""}.` : "."} Still further along than day one.
          </p>
        )}

        <h3 className="mt-6 font-head font-semibold text-[#2C363F]">Daily entries</h3>
        <table className="mt-2 w-full border-collapse text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="py-2">Date</th><th className="py-2">Dose</th><th className="py-2">Mood</th><th className="py-2">Sleep</th><th className="py-2">Symptoms</th></tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-b border-border/60 align-top">
                <td className="py-2">{l.date}</td><td className="py-2">{l.dose_mg != null ? `${l.dose_mg} mg` : "—"}</td>
                <td className="py-2">{l.mood ?? "—"}</td><td className="py-2">{l.sleep ?? "—"}</td>
                <td className="py-2">{(l.symptoms || []).join(", ") || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No entries in this period.</td></tr>}
          </tbody>
        </table>

        <p className="mt-6 text-xs text-muted-foreground">This summary is for discussion with a GP or support worker. TaperPath is educational, not medical advice.</p>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-2xl bg-muted p-4"><div className="text-xs text-muted-foreground">{label}</div><div className="font-head text-2xl font-bold text-[#2C363F]">{value}</div></div>
);
